/**
 * RealMembershipCertificateAdapter — the production `MembershipCertificatePort`
 * adapter (049). Replaces `DemoMembershipCertificateAdapter` (FR-007): the
 * certificate is now a genuine, server-generated PDF, never a self-disclosed
 * demo `.txt`.
 *
 * `getCertificate()` — the on-screen fields — calls
 * `member.certificate.preview`, which runs the SAME assembly the PDF is
 * rendered from. This adapter derives NOTHING itself: no fee-status rollup, no
 * name normalization, no date formatting. That is the point (O-021, resolved
 * 2026-09-24). It previously read the profile plus the AGGREGATE
 * `getFeeStatus().state` and assembled the fields here, so the screen and the
 * document were two derivations of one concept and could disagree — a
 * non-payable unpaid invoice read "unpaid" on screen but `Maksettu` in the
 * PDF, and a `status-unavailable` invoice showed a status on screen while the
 * download was blocked. One concept, one derivation (P8).
 *
 * A blocked assembly (FR-008) now blocks the PREVIEW too, so the section
 * renders `profile.certificate.unavailable` rather than showing fields above a
 * download button that could only ever fail.
 *
 * `obtainDocument()` calls the `member.certificate.download` mutation (049,
 * `apps/api/src/modules/membership-certificate/router.ts`): the server
 * assembles the same two reads, renders a real PDF, audits the generation,
 * and returns it base64-encoded. This adapter decodes the base64 into a
 * `Blob('application/pdf')` and mints an object URL — the consumer
 * (`MembershipCertificateSection.tsx`) owns the URL lifecycle exactly as the
 * demo adapter's contract already specified.
 *
 * Both `member` and `member.certificate` are OPTIONAL on the published
 * `AppRouter` (the conditional-spread posture — mounted only when the IS
 * client is configured). The adapter fails loudly on an absent namespace or a
 * server-side FR-008 block — never a fabricated or partial certificate (P7).
 * NOTE: the absent-namespace guard narrows the optional TYPE; on a real tRPC
 * proxy client every property access yields a proxy, so at runtime an
 * unmounted procedure surfaces as a server tRPC error instead (rendered by
 * the section as `downloadError`).
 */

import type { WebTrpcClient } from '../trpc/client.js';
import type {
  CertificateDocument,
  MembershipCertificate,
  MembershipCertificatePort,
} from './MembershipCertificatePort.js';

/**
 * Upper bound on one generation round-trip. A stalled (not failed) request
 * must reject into the section's honest FR-008 error state rather than leave
 * the download control busy indefinitely (fetch has no default timeout).
 */
export const CERTIFICATE_REQUEST_TIMEOUT_MS = 30_000;

/** The only document type this port delivers, pinned client-side (not taken from the wire). */
const CERTIFICATE_MIME_TYPE = 'application/pdf';

/** Fixed download name, pinned client-side so the server response cannot choose it. */
const CERTIFICATE_FILENAME = 'jasentodistus.pdf';

/** Generous ceiling (~7.5 MB decoded) for a small fixed-layout certificate PDF. */
const CERTIFICATE_MAX_BASE64_LENGTH = 10 * 1024 * 1024;

/** `%PDF-` — every real PDF starts with these five bytes. */
function startsWithPdfMagic(bytes: Uint8Array): boolean {
  return [0x25, 0x50, 0x44, 0x46, 0x2d].every((byte, index) => bytes[index] === byte);
}

/** `atob` + byte-copy — mirrors `AdminFeedback.tsx` / `AnalyticsExport.tsx`'s `decodeBase64`. */
function decodeBase64(contents: string): Uint8Array<ArrayBuffer> {
  const binary = atob(contents);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

export class RealMembershipCertificateAdapter implements MembershipCertificatePort {
  readonly #client: WebTrpcClient;

  constructor(client: WebTrpcClient) {
    this.#client = client;
  }

  /** Narrow the optional `member.certificate` namespace once, for both calls. */
  #certificate(): NonNullable<NonNullable<WebTrpcClient['member']>['certificate']> {
    const certificate = this.#client.member?.certificate;
    if (certificate === undefined) {
      throw new Error(
        'RealMembershipCertificateAdapter: member.certificate is absent on the AppRouter — ' +
          'the certificate surface requires the IntegrationService client to be configured (049).',
      );
    }
    return certificate;
  }

  async getCertificate(): Promise<MembershipCertificate> {
    // The server's assembled content, verbatim — the SAME fields the PDF is
    // rendered from. An FR-008 block rejects (PRECONDITION_FAILED) and the
    // section's PortErrorBoundary renders `profile.certificate.unavailable`.
    const content = await this.#certificate().preview.query(undefined, {
      signal: AbortSignal.timeout(CERTIFICATE_REQUEST_TIMEOUT_MS),
    });
    return {
      // The server's print date (the Europe/Helsinki calendar day). The PDF
      // stamps its own at generation time, so a page left open past midnight
      // can show yesterday's date above a document dated today — the document
      // is the authoritative artefact.
      printDate: content.printDate,
      memberName: content.memberName,
      memberNumber: content.memberNumber,
      membershipStartDate: content.membershipStartDate,
      feeStatus: content.feeStatus,
    };
  }

  async obtainDocument(): Promise<CertificateDocument> {
    // No input: the member is the session subject (server-derived, never
    // client-supplied). A server-side FR-008 block surfaces as a thrown
    // TRPCError; this adapter does not catch it — the section's error state
    // (FR-008) handles it, never rendering a partial/fabricated document.
    const result = await this.#certificate().download.mutate(undefined, {
      signal: AbortSignal.timeout(CERTIFICATE_REQUEST_TIMEOUT_MS),
    });
    // Pin the envelope at runtime, not only through the compile-time AppRouter
    // type: an unexpected encoding/MIME or an oversized payload rejects into the
    // section's honest downloadError arm instead of minting a mistyped blob.
    // The filename is pinned client-side; the wire field must agree, so a
    // server-side rename cannot drift silently from what the member receives.
    if (
      result.encoding !== 'base64' ||
      result.mimeType !== CERTIFICATE_MIME_TYPE ||
      result.filename !== CERTIFICATE_FILENAME
    ) {
      throw new Error('RealMembershipCertificateAdapter: unexpected document envelope');
    }
    if (result.contents.length > CERTIFICATE_MAX_BASE64_LENGTH) {
      throw new Error('RealMembershipCertificateAdapter: certificate document too large');
    }
    const bytes = decodeBase64(result.contents);
    if (!startsWithPdfMagic(bytes)) {
      throw new Error('RealMembershipCertificateAdapter: document is not a PDF');
    }
    const blob = new Blob([bytes], { type: CERTIFICATE_MIME_TYPE });
    const url = URL.createObjectURL(blob);
    return { kind: 'authoritative', filename: CERTIFICATE_FILENAME, url };
  }
}
